import React from 'react';

import { Button, ButtonProps, IconButton, IconButtonProps, Tooltip } from '@material-ui/core';
import * as MuiIcons from '@material-ui/icons';
import * as Database from '../Database';
import * as Navigation from '../Navigation';
import { Styler } from '..';

export enum Direction {
    Backward = 'backward',
    Forward = 'forward',
    Lateral = 'lateral',
    Settings = 'settings'
}

export enum Modes {
  Selected = 'selected',
  Enabled = 'available',
  Disabled = 'disabled',
  Busy = 'busy'
};

export interface Plugin {
    class: Direction,
    id: string,
    label: (truncate?: { maxChars?: number }) => JSX.Element|string|undefined,
    icon: (props: { mode: Modes }) => JSX.Element,
    onClick?: () => Promise<any>
}

export function AsButton(props: ButtonProps & {
    control: Plugin,
    busy?: boolean,
    refObject?: React.RefObject<HTMLButtonElement>
}) {
    const mode = props.busy ? Modes.Busy : !props.control.onClick ? Modes.Disabled : Modes.Enabled ;
    return (
      <Styler.Button
        id={props.control.id} 
        { ...props }
        ref={props.refObject}
        variant={
          props.control.class === Direction.Backward ? 'outlined'
          : props.control.class === Direction.Forward ? 'contained'
          : 'text'
        }
        mode={
          !props.control.onClick || props.busy ? 'disabled' 
          : props.control.class === Direction.Forward ? 'forward' 
          : props.control.class === Direction.Backward ? 'backward'
          : undefined
        }
        startIcon={props.control.class === Direction.Backward ? props.control.icon({ mode }) : undefined} 
        endIcon={props.control.class !== Direction.Backward ? props.control.icon({ mode }) : undefined}
        onClick={props.control.onClick === undefined ? undefined : (e) => { e.preventDefault(); props.control.onClick && props.control.onClick() }}        
      >{props.control.label()}</Styler.Button>
    )
  }
  
  export function AsIconButton(props: IconButtonProps & {
    mode?: 'backward'|'forward',
    control: Plugin,
    busy?: boolean,
    refObject?: React.RefObject<HTMLButtonElement>
  }) {
    const mode = props.busy ? Modes.Busy : !props.control.onClick ? Modes.Disabled : Modes.Enabled ;
    return (
      <Styler.IconButton 
        id={props.control.id} 
        { ...props }
        ref={props.refObject}
        mode={
          !props.control.onClick || props.busy ? 'disabled' 
          : props.control.class === Direction.Forward ? 'forward' 
          : props.control.class === Direction.Backward ? 'backward'
          : undefined
        }
        onClick={props.control.onClick === undefined ? undefined : (e) => { e.preventDefault(); props.control.onClick && props.control.onClick() }}
      ><Tooltip title={props.control.label() || ""}>{props.control.icon({ mode })}</Tooltip></Styler.IconButton>
    )
  }
  
  export function AsRow(props: {
    controls: Plugin[],
    style?: React.CSSProperties,
    render: (props: { key: string, control: Plugin, busy?: boolean }) => JSX.Element
  }) {
    const [ inProgress, setInProgress ] = React.useState<boolean>(false);
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        width: '100%',
        justifyContent: 'center',
        ...props.style
      }}>
       { props.controls.filter(control => control.class === Direction.Backward).map(control => 
            props.render({ key: control.id, control: {
              ...control,
              onClick: () => {
                setInProgress(true);
                const done = !control.onClick ? false : control.onClick();
                if(!!done) {
                  return done.then(() => setInProgress(false)).catch(err => { console.error(err); setInProgress(false) });
                } else {
                  setInProgress(false)
                  return Promise.resolve();
                }
              }
            }, busy: inProgress })
      ) } 
       { props.controls.filter(control => control.class === Direction.Forward).map(control => 
            props.render({ key: control.id, control: {
              ...control,
              onClick: () => {
                setInProgress(true);
                const done = !control.onClick ? false : control.onClick();
                if(!!done) {
                  return done.then(() => setInProgress(false)).catch(err => { console.error(err); setInProgress(false) });
                } else {
                  setInProgress(false)
                  return Promise.resolve();
                }
              }
            }, busy: inProgress })
      ) } 
      </div>
    )
  }
  
export function Generic(props: {
    filter: (control: Plugin) => boolean,
    render: (control: Plugin) => JSX.Element,
    on: {
        cancel?: (controller: Navigation.Controller) => Promise<any>,
        del?: (controller: Navigation.Controller) => Promise<any>,
        download?: (controller: Navigation.Controller) => Promise<any>,
        edit?: (controller: Navigation.Controller) => Promise<any>,
        save?: (controller: Navigation.Controller) => Promise<any>,
        revert?: (controller: Navigation.Controller) => Promise<any>,
        upload?: (controller: Navigation.Controller) => Promise<any>,
    },
    prefix?: string
}): JSX.Element {
    const controller = React.useContext(Navigation.Context);
    const { zone } = React.useContext(Database.Context);
    const result: Plugin[] = [];
    const prefix = props.prefix || 'views-ofcontrols-';
    const { cancel, del, download, edit, save, revert, upload } = props.on;
    if(cancel) result.push({
      class: Direction.Backward,
      onClick: () => cancel(controller),
      id: prefix+'cancel',
      label: () => 'Cancel',
      icon: () => <MuiIcons.CancelOutlined/>
  })
  if(del) result.push({
      class: Direction.Forward,
      id: prefix+'delete',
      label: () => 'Delete',
      icon: () => <MuiIcons.DeleteOutlined/>,
      onClick: () => del(controller),
  })
  if(download) result.push({
    class: Direction.Forward,
    id: prefix+'download',
    label: () => 'Download',
    icon: () => <MuiIcons.CloudDownloadOutlined/>,
    onClick: () => download(controller),
  })
  if(edit) result.push({
      class: Direction.Forward,
      onClick: () => edit(controller),
      id: prefix+'edit',
      label: () => 'Edit',
      icon: () => <MuiIcons.EditOutlined/>
  });
  if(save) result.push({
      class: Direction.Forward,
      onClick: () => save(controller),
      id: prefix+'save',
      label: () => 'Save',
      icon: () => <MuiIcons.CheckOutlined/>
  });
  if(revert) result.push({
      class: Direction.Backward,
      onClick: () => revert(controller),
      id: 'revert',
      label: () => 'Revert',
      icon: () => <MuiIcons.UndoOutlined/>
  })
  if(upload) result.push({
    class: Direction.Forward,
    id: prefix+'upload',
    label: () => 'Upload',
    icon: () => <MuiIcons.CloudUploadOutlined/>,
    onClick: () => upload(controller),
  })

  return (<React.Fragment>
      { result.filter(props.filter).map(props.render) }
  </React.Fragment>);
}

export function AsIcon(props: {
  view: Plugin,
  busy?: boolean,
  style?: React.CSSProperties
}) {
  const mode = props.busy ? Modes.Busy : !props.view.onClick ? Modes.Disabled : Modes.Enabled;
  return props.view.icon({ mode }) || <React.Fragment></React.Fragment>;
}
